import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import DatadogApi from '@/common/apis/DatadogApi';
import allPollens from '@/common/components/controls/targeting/pollen/PollenTargetingEnumerations';
import allUVs from '@/common/components/controls/targeting/uv/UVTargetingEnumerations';
import * as WeatherTargetingEnumerations from '@/common/components/controls/targeting/weather/WeatherTargetingEnumerations';
import BudgetModes from '@/common/enums/budget-modes.enum';
import CreativeOrientationType from '@/common/enums/CreativeOrientationType.enum';
import FeeType from '@/common/enums/FeeType.enum';
import PurchaseTypes from '@/common/enums/purchase-types.enum';
import TargetingCriteriaType from '@/common/enums/TargetingCriteriaType.enum';
import {
  Account,
  Advertiser,
  Campaign,
  Market,
  MediaType,
  Package,
  PlanProperty,
  PurchaseTypeOptions,
  UnitPack,
} from '@/common/models';
import { IEstimatedReachTargetParams } from '@/common/models/audience/ReachStatistics';
import TTDCampaign from '@/common/models/campaign/TTDCampaign';
import { ICPMNetAPI } from '@/common/models/cpm/CpmNetApi.model';
import CPMCalculator from '@/common/models/CPMCalculator';
import { daypartToRanges } from '@/common/models/Dayparting';
import Exception from '@/common/models/Exception';
import IFromPackage from '@/common/models/package/IFromPackage';
import { getEntityProperties } from '@/common/models/plan/Plan.entityProperties';
import PlanApproval from '@/common/models/plan/PlanApproval';
import { IPlanGoalOptions } from '@/common/models/plan/PlanGoalOptions';
import PlanHeatmap from '@/common/models/plan/PlanHeatmap';
import PlanPropertyKeys from '@/common/models/plan/PlanPropertyKeys';
import { ICpmTargetParams } from '@/common/models/plan/types/CpmTargetParams';
import { IMultiDealsParams } from '@/common/models/plan/types/MultiDealsParams';
import { IPlanAPI, IPlanFeeAPI } from '@/common/models/plan/types/PlanAPI';
import PlanTargetingCriterion from '@/common/models/plan/types/PlanTargetingCriterion';
import { ITargetParams } from '@/common/models/plan/types/TargetParams';
import { ITargetParamsEstimatedDeliveryStatistics } from '@/common/models/plan/types/TargetParamsEstimatedDeliveryStatistics';
import { FilterCollection, FilterComparisonOperator, QueryParams } from '@/common/models/query-params';
import SharedPublicEntity from '@/common/models/SharedPublicEntity';
import { NetworkFrequencyCap } from '@/common/models/targeting-criteria/NetworkFrequencyCap';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import { DASH, FullDaypart, toServerDate, toServerDateTime } from '@/globals';
import DisplayAndVideoSSPs from '@/org-app/components/lineItems/form/DisplayAndVideoSSPs';
import { TBreakdownTypeAPI } from '@/org-app/components/plans/budgetBreakdown/BudgetBreakdownTypes';
import rootStore from '@/store';
import { UPDATE_PLAN_CPM_CALCULATOR, UPDATE_PLAN_PACKAGE } from '@/store/modules/models/mutation-types';
import rootState from '@/store/state';
import { getDiff } from '@/utils/Diff';
import formatNumber from '@/utils/NumberFormatter';
import { mapOrgAppState } from '@/utils/StateMapping';
import Validatable, { IEntityChange, IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

const type = 'Plan';

export default class Plan extends Validatable<Plan> implements IFromPackage {
  apiObject: IPlanAPI;
  cachedOriginalObject: Plan;
  static marketsCache: Market[] = null;
  static isGettingMarkets = false;
  static mediaTypesCache: MediaType[] = null;
  static isGettingMediaTypes = false;
  static unitPacksCache: UnitPack[] = null;
  static isGettingUnitPacks = false;

  type: string;
  name: string;
  uuid: string;
  startDate: DateTime;
  endDate: DateTime;
  budget: number;
  budgetMode: BudgetModes;
  cpmNet: ICPMNetAPI[];
  CPM: number;
  cpmMarkup: number;
  planFee: IPlanFeeAPI;
  isCPMFromRateCard: boolean;
  targetImpressions: number;
  adDuration: number;
  daypart: string;
  demographicIDs: number[];
  planProperties: PlanProperty[];
  locationIDs: number[];
  locationExclusionIDs: number[];
  geofenceGroupIDs: number[];
  geofenceGroupExclusionIDs: number[];
  languageIDs: number[];
  languageExclusionIDs: number[];
  marketIDs: number[];
  marketExclusionIDs: number[];
  mediaTypeIDs: number[];
  mediaTypeExclusionIDs: number[];
  networkIDs: number[];
  networkExclusionIDs: number[];
  siteIDs: number[];
  siteExclusionIDs: number[];
  unitIDs: number[];
  unitExclusionIDs: number[];
  unitTags: string[];
  unitTagExclusions: string[];
  unitPackIDs: number[];
  unitPackExclusionIDs: number[];
  creativeFormats: string[];
  creativeFormatExclusions: string[];
  creativeOrientations: CreativeOrientationType[];
  creativeOrientationExclusions: CreativeOrientationType[];
  iabCategories: string[];
  iabCategoryExclusions: string[];
  mediaOwnerIDs: number[];
  organizationID: number;
  organizationDSPID: number;
  minConcentrationFactor: number;
  minConcentrationValue: number;
  minSecondsBetweenPlays: number;
  purchaseType: PurchaseTypes;
  accountID: number;
  advertiserID: number;
  createdOn: DateTime;
  modifiedOnUtc: DateTime;
  networkFrequencyCaps: NetworkFrequencyCap[];
  createdByUserID: number;
  heatmaps: any[];
  planApprovals: any[];
  deal: any;
  packageGroupID: number;
  packageGroupDealsIDs: number[];
  packageGroupDealsIDsMapping: any;
  breakdownType: TBreakdownTypeAPI;
  parentPlanID: number;
  children: any[];
  isInlineEdited: boolean;
  estimatedAudienceCPM: number;
  estimatedCPM: number;
  estimatedAverageAudienceCPM: number;
  estimatedAverageCPM: number;
  cpmCalculator: CPMCalculator;
  timestamp: DateTime;
  sharedEntity?: SharedPublicEntity;
  advertiser: Advertiser;
  account: Account;
  maxBidCPM: number;
  packageInternal: Package;
  planTargetingCriteria: PlanTargetingCriterion[];
  campaigns?: Campaign[];
  sharedPlanID?: number;
  isMeasurementRequested: boolean;
  packageID?: number;
  buyFromDealsEnabled: boolean;
  ttdCampaign: TTDCampaign;
  fromPlanID: number;
  goal: IPlanGoalOptions;
  journeyWorkflowConfigurationId: number;

  constructor(
    id = -1,
    name = '',
    uuid: string,
    startDate: DateTime,
    endDate: DateTime,
    budget = 0,
    budgetMode = BudgetModes.BUDGET,
    cpmNet: ICPMNetAPI[],
    cpmGross: number,
    cpmMarkup: number, // Deprecated but still being used by old plans
    planFee: IPlanFeeAPI = null,
    isCPMFromRateCard = true,
    targetedImpressions = 0,
    adDuration: number,
    daypart = FullDaypart,
    demographicIDs: number[] = [],
    planProperties: PlanProperty[] = [],
    locationIDs: number[] = [],
    locationExclusionIDs: number[] = [],
    geofenceGroupIDs: number[] = [],
    geofenceGroupExclusionIDs: number[] = [],
    marketIDs: number[] = [],
    marketExclusionIDs: number[] = [],
    mediaTypeIDs: number[] = [],
    mediaTypeExclusionIDs: number[] = [],
    networkIDs: number[] = [],
    networkExclusionIDs: number[] = [],
    siteIDs: number[] = [],
    siteExclusionIDs: number[] = [],
    unitIDs: number[] = [],
    unitExclusionIDs: number[] = [],
    unitTags: string[] = [],
    unitTagExclusions: string[] = [],
    unitPackIDs: number[] = [],
    unitPackExclusionIDs: number[] = [],
    creativeFormats: string[] = [],
    creativeFormatExclusions: string[] = [],
    creativeOrientations: CreativeOrientationType[] = [],
    creativeOrientationExclusions: CreativeOrientationType[] = [],
    iabCategories: string[] = [],
    iabCategoryExclusions: string[] = [],
    mediaOwnerIDs: number[] = [],
    organizationID: number,
    organizationDSPID: number = null,
    minConcentrationFactor = 0,
    minConcentrationValue = 0,
    minSecondsBetweenPlays = 0,
    purchaseType: PurchaseTypes = null,
    accountID: number,
    advertiserID: number,
    createdOn: DateTime,
    modifiedOnUtc: DateTime,
    networkFrequencyCaps = [],
    createdByUserID: number,
    heatmaps = [],
    planApprovals = [],
    deal: Package = null,
    packageGroupID: number = null,
    packageGroupDealsIDs: number[] = null,
    packageGroupDealsIDsMapping: any = null,
    breakdownType: TBreakdownTypeAPI = null,
    parentPlanID: number = null,
    children = [],
    packageInternal: Package = null,
    planTargetingCriteria: PlanTargetingCriterion[] = [],
    languageIDs: number[] = [],
    languageExclusionIDs: number[] = [],
    isMeasurementRequested = false,
    packageID = null,
    buyFromDealsEnabled = false,
    ttdCampaign = null,
    goal = null,
    journeyWorkflowConfigurationId: number = null,
  ) {
    super();

    this.type = type;
    this.id = id;
    this.name = name;
    this.uuid = uuid;
    this.startDate = startDate;
    this.endDate = endDate;
    this.budget = budget;
    this.budgetMode = budgetMode;
    this.cpmNet = cpmNet;
    this.CPM = cpmGross == null ? null : cpmGross;
    this.cpmMarkup = cpmMarkup;
    this.planFee = planFee;
    this.isCPMFromRateCard = isCPMFromRateCard;
    this.targetImpressions = targetedImpressions;
    this.adDuration = adDuration;
    this.daypart = daypart;
    this.demographicIDs = demographicIDs;
    this.planProperties = planProperties;

    this.locationIDs = locationIDs;
    this.locationExclusionIDs = locationExclusionIDs;
    this.geofenceGroupIDs = geofenceGroupIDs;
    this.geofenceGroupExclusionIDs = geofenceGroupExclusionIDs;
    this.languageIDs = languageIDs;
    this.languageExclusionIDs = languageExclusionIDs;
    this.marketIDs = marketIDs;
    this.marketExclusionIDs = marketExclusionIDs;
    this.mediaTypeIDs = mediaTypeIDs;
    this.mediaTypeExclusionIDs = mediaTypeExclusionIDs;
    this.networkIDs = networkIDs;
    this.networkExclusionIDs = networkExclusionIDs;
    this.siteIDs = siteIDs;
    this.siteExclusionIDs = siteExclusionIDs;
    this.unitIDs = unitIDs;
    this.unitExclusionIDs = unitExclusionIDs;
    this.unitTags = unitTags;
    this.unitTagExclusions = unitTagExclusions;
    this.unitPackIDs = unitPackIDs;
    this.unitPackExclusionIDs = unitPackExclusionIDs;
    this.creativeFormats = creativeFormats;
    this.creativeFormatExclusions = creativeFormatExclusions;
    this.creativeOrientations = creativeOrientations;
    this.creativeOrientationExclusions = creativeOrientationExclusions;
    this.iabCategories = iabCategories;
    this.iabCategoryExclusions = iabCategoryExclusions;
    this.mediaOwnerIDs = mediaOwnerIDs;
    this.organizationID = organizationID;
    this.organizationDSPID = organizationDSPID;
    this.minConcentrationFactor = minConcentrationFactor;
    this.minConcentrationValue = minConcentrationValue;
    this.minSecondsBetweenPlays = minSecondsBetweenPlays;
    this.purchaseType =
      purchaseType || (Plan.isSupplyOrganization ? PurchaseTypes.GUARANTEED : PurchaseTypes.NON_GUARANTEED);
    this.accountID = accountID;
    this.advertiserID = advertiserID;
    this.createdOn = createdOn;
    this.modifiedOnUtc = modifiedOnUtc;
    this.networkFrequencyCaps = networkFrequencyCaps;
    this.createdByUserID = createdByUserID;
    this.heatmaps = heatmaps;
    this.planApprovals = planApprovals;

    this.isInlineEdited = false;

    this.estimatedAudienceCPM = null;
    this.estimatedCPM = null;
    this.estimatedAverageAudienceCPM = null;
    this.estimatedAverageCPM = null;
    this.cpmCalculator = new CPMCalculator(null, null, this);

    this.timestamp = DateTime.local();
    this.sharedEntity = null;

    if (this.mediaOwnerIDs.length === 0 && Plan.isSupplyOrganization) {
      this.mediaOwnerIDs = [rootState.orgApp.organization.id];
    }

    this.deal = deal;
    this.packageGroupID = packageGroupID;
    this.packageGroupDealsIDs = packageGroupDealsIDs;
    this.packageGroupDealsIDsMapping = packageGroupDealsIDsMapping;

    this.breakdownType = breakdownType;
    this.parentPlanID = parentPlanID;
    this.children = children;

    this.packageInternal = packageInternal;
    this.planTargetingCriteria = planTargetingCriteria;

    this.isMeasurementRequested = isMeasurementRequested;
    this.packageID = packageID;
    this.buyFromDealsEnabled = buyFromDealsEnabled;
    this.fromPlanID = null;
    this.goal = goal;
    this.journeyWorkflowConfigurationId = journeyWorkflowConfigurationId;

    // TTD Sidekick
    this.ttdCampaign = ttdCampaign;

    this.apiObject = null;
    this.cachedOriginalObject = null;
  }

  static {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Plan.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      Plan.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      Plan.entityPropertiesCached = null;
    });
  }

  get endpoint(): string {
    return `/plans/${this.id}`;
  }

  get duplicateEndpoint() {
    return `/plans/new/edit/duplicate/${this.id}`;
  }

  get isStartDateEditable() {
    return true; // No restrictions
  }

  get isEndDateEditable() {
    return true; // No restrictions
  }

  get hasDurationRestriction(): boolean {
    return false; // No restrictions
  }

  get isBudgetTotalAmountAvailable(): boolean {
    return true; // No restrictions
  }

  get areTargetImpressionsAvailable(): boolean {
    return true; // No restrictions
  }

  get properties(): PlanProperty[] {
    return this.planProperties;
  }

  get isBudgetModeAmount(): boolean {
    return this.budgetMode === BudgetModes.BUDGET;
  }

  get isBudgetModeImpressions(): boolean {
    return this.budgetMode === BudgetModes.IMPRESSIONS;
  }

  static get isSupplyOrganization(): boolean {
    return mapOrgAppState()?.organization?.isSupplyOrganization ?? false;
  }

  static get isDemandOrganization(): boolean {
    return mapOrgAppState()?.organization?.isDemandOrganization ?? false;
  }

  static get isTTDOrganization(): boolean {
    return mapOrgAppState()?.organization?.isTTDOrganization;
  }

  static get organizationSettings(): any {
    return mapOrgAppState()?.organizationSettings;
  }

  get startDateFormatted(): string {
    return this.startDate?.toLocaleString(DateTime.DATE_SHORT) ?? DASH;
  }

  get endDateFormatted(): string {
    return this.endDate?.toLocaleString(DateTime.DATE_SHORT) ?? DASH;
  }

  get minBidCPMFromLineItemProperties(): number {
    const property = this.planProperties.find((x) => x.key === PlanPropertyKeys.MIN_BID_CPM);
    return property ? Number.parseFloat(property.value) : null;
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) ?? DASH;
  }

  get createdOnFormatted(): string {
    return this.createdOn?.toLocal().toLocaleString(DateTime.DATETIME_MED) ?? DASH;
  }

  get advertiserName(): string {
    return this.advertiser?.name ?? DASH;
  }

  get ttdAdvertiserName(): string {
    return this.ttdCampaign?.advertiserName ?? DASH;
  }

  get advertiserDomain(): string {
    return this.advertiser?.domain ?? this.advertiser?.uniqueAdvertiserDomain;
  }

  get advertiserDescription(): string {
    return this.advertiser?.uniqueAdvertiser != null
      ? `${this.advertiserName} (${this.advertiserDomain})`
      : this.advertiser.description;
  }

  get advertiserLogo(): string {
    return this.advertiser?.uniqueAdvertiser != null ? this.advertiser.uniqueAdvertiserLogoUrl : null;
  }

  get accountLogoUrl(): string {
    return this.account ? this.account.logoUrl : null;
  }

  get minSecondsBetweenPlaysFormatted(): string {
    return `${formatNumber(this.minSecondsBetweenPlays, '0,0')} ${i18n.global.t('names.second', { n: 2 })}`;
  }

  get isOwnPlan(): boolean {
    return this.createdByUserID === rootState?.user?.id;
  }

  static get defaultPurchaseType(): PurchaseTypes {
    return PurchaseTypes.NON_GUARANTEED;
  }

  get sharedAtFormatted(): string {
    return this.sharedEntity != null
      ? DateTime.fromISO(this.sharedEntity.createdOnUtc).toLocal().toLocaleString(DateTime.DATETIME_SHORT)
      : DASH;
  }

  get multiDealsParams(): IMultiDealsParams {
    return {
      packagegroup_id: this.packageGroupDealsIDs?.length > 0 ? null : this.packageGroupID,
      package_ids: this.packageGroupDealsIDs?.length > 0 ? this.packageGroupDealsIDs : [],
    };
  }

  get targetParams(): ITargetParams {
    const obj: ITargetParams = {
      creative_orientations: this.creativeOrientations,
      creative_orientation_exclusions: this.creativeOrientationExclusions,
      iab_categories: this.iabCategories,
      iab_category_exclusions: this.iabCategoryExclusions,
      language_exclusion_ids: this.languageExclusionIDs,
      language_ids: this.languageIDs,
      location_ids: this.locationIDs,
      location_exclusion_ids: this.locationExclusionIDs,
      unit_ids: this.unitIDs,
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_tags: this.unitTags,
      unit_tag_exclusions: this.unitTagExclusions,
      unitpack_ids: this.unitPackIDs,
      unitpack_exclusion_ids: this.unitPackExclusionIDs,
      site_ids: this.siteIDs,
      site_exclusion_ids: this.siteExclusionIDs,
      geofencegroup_ids: this.geofenceGroupIDs,
      geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
      market_ids: this.marketIDs,
      market_exclusion_ids: this.marketExclusionIDs,
      max_bid_cpm: this.maxBidCPM,
      network_ids: this.networkIDs,
      network_exclusion_ids: this.networkExclusionIDs,
      demographic_ids: this.demographicIDs,
      from_date: toServerDate(this.startDate),
      to_date: toServerDate(this.endDate),
      ad_duration: this.adDuration ?? 0,
      daypart: this.daypart,
      screen_frequency_cap: this.minSecondsBetweenPlays ?? 0,
      owner_ids: this.mediaOwnerIDs,
      owner_organization_ids: this.mediaOwnerIDs,
      mediatype_ids: this.mediaTypeIDs,
      mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
      purchase_type: this.purchaseType || Plan.defaultPurchaseType,
      lineitem_networkfreqcaps: this.networkFrequencyCaps?.map((x) => x.toApi()),
      package_id: this.packageID,
      plan_id: this.id,
      creative_formats: this.creativeFormats,
      creative_format_exclusions: this.creativeFormatExclusions,
      type: 'plans',
      buy_from_deals_enabled: this.buyFromDealsEnabled,
      requested_for_utc: this.createdOn ?? DateTime.utc(),
    };

    if (rootState.orgApp?.organization?.isDemandOrganization) {
      obj.packagegroup_id = this.multiDealsParams.packagegroup_id;
      obj.package_ids = this.multiDealsParams.package_ids;
    }

    return obj;
  }

  get reachTargetParams(): IEstimatedReachTargetParams {
    return {
      ...this.targetParams,
      apply_realtime_multiplier: true,
      impression_goal: Number(this.targetImpressions),
    };
  }

  get cpmTargetParams(): ICpmTargetParams {
    const targetParams: ICpmTargetParams = {
      ad_duration: this.adDuration ?? 0,
      buyer_organization_dsp_id: this.organizationDSPID,
      buyer_organization_id: this.organizationID,
      daypart: this.daypart,
      demographic_ids: this.demographicIDs,
      from_date: toServerDate(this.startDate),
      geofencegroup_ids: this.geofenceGroupIDs,
      geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
      lineitem_id: -1,
      plan_id: this.id == null ? -1 : this.id,
      creative_orientations: this.creativeOrientations,
      creative_orientation_exclusions: this.creativeOrientationExclusions,
      iab_categories: this.iabCategories,
      iab_category_exclusions: this.iabCategoryExclusions,
      language_exclusion_ids: this.languageExclusionIDs,
      language_ids: this.languageIDs,
      location_exclusion_ids: this.locationExclusionIDs,
      location_ids: this.locationIDs,
      market_ids: this.marketIDs,
      market_exclusion_ids: this.marketExclusionIDs,
      max_bid_cpm: this.maxBidCPM,
      measurement_requested: this.isMeasurementRequested,
      mediatype_ids: this.mediaTypeIDs,
      mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
      min_concentration_factor: this.minConcentrationFactor,
      network_ids: this.networkIDs,
      network_exclusion_ids: this.networkExclusionIDs,
      owner_ids: this.mediaOwnerIDs,
      owner_organization_ids: this.mediaOwnerIDs,
      package_exchange_id: null,
      package_organization_id: null,
      purchase_type: this.purchaseType || Plan.defaultPurchaseType,
      screen_frequency_cap: this.minSecondsBetweenPlays ?? 0,
      site_exclusion_ids: this.siteExclusionIDs,
      site_ids: this.siteIDs,
      to_date: toServerDate(this.endDate),
      unique_advertiser_id_to_check: this.advertiser?.uniqueAdvertiser?.id || null,
      unit_exclusion_ids: this.unitExclusionIDs,
      unit_ids: this.unitIDs,
      unit_tags: this.unitTags,
      unit_tag_exclusions: this.unitTagExclusions,
      unitpack_ids: this.unitPackIDs,
      unitpack_exclusion_ids: this.unitPackExclusionIDs,
      lineitem_networkfreqcaps: this.networkFrequencyCaps?.map((x) => x.toApi()),
      creative_formats: this.creativeFormats,
      creative_format_exclusions: this.creativeFormatExclusions,
      organizationID: this.organizationID,
      type: 'plans',
      package_id: this.packageID,
      buy_from_deals_enabled: this.buyFromDealsEnabled,
      requested_for_utc: this.createdOn ?? DateTime.utc(),
    };

    if (this.minConcentrationValue !== null) {
      targetParams.min_concentration_value = this.minConcentrationValue;
    }

    if (Plan.isDemandOrganization) {
      targetParams.packagegroup_id = this.multiDealsParams.packagegroup_id;
      targetParams.package_ids = this.multiDealsParams.package_ids;

      if (this.targetingCriteria?.length !== 0) {
        targetParams.targetingcriterion_ids = this.targetingCriteria.map((t) => t.targetingCriterionID);
      }
    }

    return targetParams;
  }

  get targetParamsEstimatedDeliveryStatistics(): ITargetParamsEstimatedDeliveryStatistics {
    const obj: ITargetParamsEstimatedDeliveryStatistics = {
      from_date: toServerDate(this.startDate),
      to_date: toServerDate(this.endDate),
      organization_id: this.organizationID,
      ad_duration: this.adDuration ?? 0,
      unit_targeting: {
        unique_advertiser_id_to_check: this.advertiser?.uniqueAdvertiser?.id || null,
        lineitem_id: this.id, // TO BE REMOVED
        plan_id: this.id,
        purchase_type: this.purchaseType || Plan.defaultPurchaseType,
        organization_id: this.organizationID,
        owner_ids: this.mediaOwnerIDs,
        creative_orientations: this.creativeOrientations,
        creative_orientation_exclusions: this.creativeOrientationExclusions,
        iab_categories: this.iabCategories,
        iab_category_exclusions: this.iabCategoryExclusions,
        language_exclusion_ids: this.languageExclusionIDs,
        language_ids: this.languageIDs,
        location_ids: this.locationIDs,
        location_exclusion_ids: this.locationExclusionIDs,
        geofencegroup_ids: this.geofenceGroupIDs,
        geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
        mediatype_ids: this.mediaTypeIDs,
        mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
        market_ids: this.marketIDs,
        market_exclusion_ids: this.marketExclusionIDs,
        network_ids: this.networkIDs,
        network_exclusion_ids: this.networkExclusionIDs,
        site_ids: this.siteIDs,
        site_exclusion_ids: this.siteExclusionIDs,
        unit_ids: this.unitIDs,
        unit_exclusion_ids: this.unitExclusionIDs,
        creative_formats: this.creativeFormats,
        creative_format_exclusions: this.creativeFormatExclusions,
        unit_tags: this.unitTags,
        unit_tag_exclusions: this.unitTagExclusions,
        unitpack_ids: this.unitPackIDs,
        unitpack_exclusion_ids: this.unitPackExclusionIDs,
        package_id: this.packageID,
        buy_from_deals_enabled: this.buyFromDealsEnabled,
        requested_for_utc: this.createdOn ?? DateTime.utc(),
      },
    };

    if (this.daypart != null) obj.daypart = this.daypart;
    if (this.demographicIDs != null) obj.demographic_ids = this.demographicIDs;
    if (this.minSecondsBetweenPlays != null) {
      obj.screen_frequency_cap = this.minSecondsBetweenPlays;
    }
    if (this.networkFrequencyCaps != null) obj.network_frequency_caps = this.networkFrequencyCaps.map((x) => x.toApi());
    if (this.maxBidCPM != null) obj.max_bid_cpm = this.maxBidCPM;

    if (this.targetingCriteria?.length) {
      obj.targetingcriterion_ids = this.targetingCriteria.map((t) => t.targetingCriterionID);
    }

    if (rootState.orgApp?.organization?.isDemandOrganization) {
      obj.unit_targeting = {
        ...obj.unit_targeting,
        ...this.multiDealsParams,
      };
    }

    return obj;
  }

  get adDurationFormatted(): string {
    return `${formatNumber(this.adDuration, '0,0')} ${i18n.global.t('names.second', { n: 2 })}`;
  }

  get budgetFormatted(): string {
    return this.cpmCalculator.budgetFormatted;
  }

  get budgetFormattedShort(): string {
    return this.cpmCalculator.budgetFormattedShort;
  }

  get targetImpressionsFormatted(): string {
    return this.cpmCalculator.estimatedTargetImpressionsFormatted;
  }

  get hasMarkup() {
    return this.cpmMarkup != null || this.planFee != null;
  }

  get isPercentMarkup() {
    return this.cpmMarkup == null && this.planFee?.type === FeeType.Percent;
  }

  get isOverrideMarkup() {
    return this.cpmMarkup != null || this.planFee?.type === FeeType.Override;
  }

  get planFeeValue() {
    return this.planFee?.value ?? this.cpmMarkup;
  }

  get cpmMarkupFormatted(): string {
    if (this.hasMarkup && this.planFeeValue != null) {
      return this.isPercentMarkup ? `${this.planFeeValue}%` : i18n.global.n(this.planFeeValue, 'currency');
    }
    return DASH;
  }

  get isDaypartFull(): boolean {
    return this.daypart?.indexOf('0') === -1;
  }

  get isDaypartEmpty(): boolean {
    return this.daypart?.indexOf('1') === -1;
  }

  get isDaypartEmptyOrFull(): boolean {
    return this.isDaypartEmpty || this.isDaypartFull;
  }

  get accountName() {
    return this.account?.name ?? DASH;
  }

  get activeNetworkFrequencyCaps() {
    const { networks } = rootState.orgApp.ReferenceData;
    return this.networkFrequencyCaps?.filter((nfc) => networks?.some((x) => nfc.networkId === x.id));
  }

  get frequencyCapPerNetwork(): string {
    const { networks } = rootState.orgApp.ReferenceData;
    if (
      networks == null ||
      networks.length === 0 ||
      this.activeNetworkFrequencyCaps == null ||
      this.activeNetworkFrequencyCaps.length === 0
    ) {
      return DASH;
    }

    return this.activeNetworkFrequencyCaps
      .map((nfc) => {
        const network = networks.find((x) => nfc.networkId === x.id);
        return `${network.name}: ${nfc.minSecondsBetweenPlays}s`;
      })
      .join(', ');
  }

  get hasInventoryTargeting(): boolean {
    return !(
      this.marketIDs?.length === 0 &&
      this.marketExclusionIDs?.length === 0 &&
      !this.languageIDs?.length &&
      !this.languageExclusionIDs?.length &&
      this.locationIDs?.length === 0 &&
      this.locationExclusionIDs?.length === 0 &&
      this.mediaTypeIDs?.length === 0 &&
      this.mediaTypeExclusionIDs?.length === 0 &&
      this.networkIDs?.length === 0 &&
      this.networkExclusionIDs?.length === 0 &&
      this.unitIDs?.length === 0 &&
      this.unitExclusionIDs?.length === 0 &&
      this.siteIDs?.length === 0 &&
      this.siteExclusionIDs?.length === 0 &&
      this.geofenceGroupIDs?.length === 0 &&
      this.geofenceGroupExclusionIDs?.length === 0 &&
      this.unitTags?.length === 0 &&
      this.unitPackIDs?.length === 0 &&
      this.unitPackExclusionIDs?.length === 0 &&
      this.creativeFormats?.length === 0 &&
      this.creativeFormatExclusions?.length === 0 &&
      this.creativeOrientations?.length === 0 &&
      this.creativeOrientationExclusions?.length === 0 &&
      this.iabCategories?.length === 0 &&
      this.iabCategoryExclusions?.length === 0
    );
  }

  get dealIDsByMediaOwnerProperty(): PlanProperty {
    return this.planProperties.find((x) => x.key === PlanPropertyKeys.DEAL_IDS_BY_MEDIAOWNER);
  }

  // TODO this will be needed for plan to support Display Video LI
  get dealIDsBySspProperty() {
    return null;
  }

  // TODO this will be needed for plan to support Display Video LI
  get dealIDsBySsp() {
    return DASH;
  }

  // TODO this will be needed for plan to support different bidding types
  get cpmStrategy(): string {
    return null;
  }
  // TODO this will be needed for plan to support Display Video LI
  get isChannelTypeDisplayVideo(): boolean {
    return false;
  }

  get isFromCuratedDeal(): boolean {
    return !!this.planProperties.find((x) => x.key === PlanPropertyKeys.FROM_CURATED_DEAL);
  }

  get dealIDsProperty(): PlanProperty {
    return this.planProperties.find((x) => x.key === PlanPropertyKeys.DEAL_IDS);
  }

  get dealIDSSPProperty(): PlanProperty {
    return this.planProperties.find((x) => x.key === PlanPropertyKeys.DEAL_ID_SSP);
  }

  get dealIDProperty(): PlanProperty {
    return this.planProperties.find((x) => x.key === PlanPropertyKeys.DEAL_ID);
  }

  get targetingCriteria(): PlanTargetingCriterion[] {
    return this.planTargetingCriteria || [];
  }

  get isBuyingCuratedDeal(): boolean {
    return this.packageID != null;
  }

  get hasAudienceTargeting(): boolean {
    return this.planProperties.some((x) => x.key === 'beta-audience') || this.demographicIDs.length > 0;
  }

  get campaignCustomFields() {
    const customFields = rootStore.getters['orgApp/organizationCampaignCustomFields'];
    return this.planProperties.filter((x) => customFields.some((cf) => cf.key === x.key));
  }

  get daypartRanges() {
    return daypartToRanges(this.daypart);
  }

  get dealIDFormatted(): string {
    if (this.dealIDProperty !== undefined) {
      return this.dealIDProperty.value;
    }
    if (this.dealIDsProperty !== undefined && this.dealIDsProperty.value != null) {
      return this.dealIDsProperty.value.split(',').join(', ');
    }
    if (this.dealIDsBySspProperty?.value) {
      const dealIDsBySspProperty = JSON.parse(this.dealIDsBySspProperty.value);
      const deals = [];

      Object.keys(dealIDsBySspProperty).forEach((id) => {
        const ssp = DisplayAndVideoSSPs.getAvailableSSPs().find((x) => x.id === parseInt(id, 10));
        deals.push(`${ssp?.name}: ${dealIDsBySspProperty[id].join(', ')}`);
      });

      return deals.join(', ');
    }
    return this.packageInternal === null || this.packageInternal === undefined
      ? DASH
      : this.packageInternal.dealIDFormatted;
  }
  get targetCriteriaWeather() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Weather,
    );
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map(
              (v) =>
                v.value
                  .split(',')
                  .map((v2) => WeatherTargetingEnumerations.allWeathers().find((w) => w.id === v2).name) || [],
            ),
          )
          .flat()
      : null;
  }

  get targetCriteriaTemperature() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Temperature,
    );
    const tempLimits = WeatherTargetingEnumerations.allTemperatureLimits();
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${tempLimits.find((tl) => tl.id === x.code.slice(0, x.code.lastIndexOf('_')))?.name || x.code} ${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value}°C`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaUV() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.UV,
    );
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map((v) => v.value.split(',').map((v2) => allUVs().find((w) => w.id === v2).name) || []),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationWeather() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationWeather,
    );
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values.map(
              (v) =>
                v.value
                  .split(',')
                  .map((v2) => WeatherTargetingEnumerations.allWeathers().find((w) => w.id === v2).name) || [],
            ),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationTemperature() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationTemperature,
    );
    const tempLimits = WeatherTargetingEnumerations.allTemperatureLimits();
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${tempLimits.find((tl) => tl.id === x.code.slice(0, x.code.lastIndexOf('_')))?.name || x.code} ${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value}°C`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationRain() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationRain,
    );
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value} mm`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaDestinationSnow() {
    const weatherTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.DestinationSnow,
    );
    const comparators = WeatherTargetingEnumerations.allComparators();
    return weatherTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === weatherTargetingCriterion.id)
          .map((tc) =>
            tc.values
              .map(
                (x) =>
                  `${comparators.find((tl) => tl.id === x.code.slice(x.code.lastIndexOf('_') + 1))?.name || x.code} ${x.value} mm`,
              )
              .join(', '),
          )
          .flat()
      : null;
  }

  get targetCriteriaPollen() {
    const pollenTargetingCriterion = mapOrgAppState().ReferenceData.targetingCriteria.find(
      (x) => x.type === TargetingCriteriaType.Pollen,
    );
    return pollenTargetingCriterion != null
      ? this.targetingCriteria
          .filter((tc) => tc.targetingCriterionID === pollenTargetingCriterion.id)
          .map((tc) =>
            tc.values.map((v) => v.value.split(',').map((v2) => allPollens().find((w) => w.id === v2).name) || []),
          )
          .flat()
      : null;
  }

  get targetCriteriaCustomEvents() {
    const customEvents = mapOrgAppState().ReferenceData.targetingCriteria.filter((x) => x.isUpdatePushedExternally);
    const customEventSubtypes = customEvents.flatMap((ce) => ce.subTypes);
    const customEventIDs = customEvents.map((ce) => ce.id);

    return customEvents.length
      ? this.targetingCriteria
          .filter((tc) => customEventIDs.includes(tc.targetingCriterionID))
          .map((tc) =>
            tc.values.map(
              (x) =>
                `${customEventSubtypes.find((st) => st.code === x.code).categoryName}: ${customEventSubtypes.find((st) => st.code === x.code).name}`,
            ),
          )
          .flat()
      : null;
  }

  get originalObject(): Plan {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = Plan.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  isPristine(): boolean {
    if (this.isNew) return true;
    const ignoreProperties = [
      'account',
      'advertiser',
      'archived',
      'cadence',
      'campaigns',
      'children',
      'cpm',
      'cpm_gross',
      'cpm_markup',
      'cpm_net',
      'cpp',
      'created_by',
      'delivery_mode',
      'goal',
      'heatmapgroup_id',
      'heatmap_group',
      'infinite_budget',
      'inventory_targeting',
      'inventorytargeting_id',
      'modified_by',
      'organization_id',
      'packagegroup',
      'plan_approvals',
      'plan_fee',
      'plan_properties',
      'plan_targetingcriteria',
      'reach_and_frequency_status',
      'shared_entity',
      'sharedplan_id',
      'ttd_campaign',
    ];
    const diffs = getDiff(this.apiObject, this.toApi(), ignoreProperties);
    const inventoryTargetingDiffs = getDiff(this.apiObject.inventory_targeting, this.toApi().inventory_targeting, [
      'co2e_emissions_score_threshold',
      'inventorytargeting_id',
    ]);
    const preparePlanTargetingCriteria = (apiObject: IPlanAPI) => {
      return apiObject.plan_targetingcriteria.map((x) => ({
        targetingcriterion_id: x.targetingcriterion_id,
        plantargetingcriterion_values: x.plantargetingcriterion_values.map((y) => ({
          code: y.code,
          value: y.value,
        })),
      }));
    };
    const planTargetingCriteriaDiffs = getDiff(
      preparePlanTargetingCriteria(this.apiObject),
      preparePlanTargetingCriteria(this.toApi()),
    );
    if (diffs.length === 0 && inventoryTargetingDiffs.length === 0 && planTargetingCriteriaDiffs.length === 0)
      return true;
    DatadogApi.logUnexpectedChange([...diffs, ...inventoryTargetingDiffs]);
    return false;
  }

  static fromApi(apiObject: IPlanAPI): Plan {
    const obj = {
      id: apiObject.plan_id,
      name: apiObject.name,
      uuid: apiObject.uuid,
      startDate: DateTime.fromISO(apiObject.start_date),
      endDate: DateTime.fromISO(apiObject.end_date),
      budget: apiObject.budget,
      budgetMode: apiObject.budget_mode,
      CPM: apiObject.cpm,
      cpmNet: apiObject.cpm_net,
      cpmMarkup: apiObject.cpm_markup,
      planFee: apiObject.plan_fee,
      isCPMFromRateCard: apiObject.is_cpm_from_rate_card,
      targetImpressions: apiObject.targeted_impressions,
      demographicIDs: apiObject.demographic_ids || [],
      planProperties:
        apiObject.plan_properties !== undefined
          ? apiObject.plan_properties
              .filter((x) => x.organization_id == null || x.organization_id === rootState.orgApp.organization.id)
              .map((x) => PlanProperty.fromApi(x))
          : [],
      creativeOrientations: apiObject.inventory_targeting?.creative_orientations || [],
      creativeOrientationExclusions: apiObject.inventory_targeting?.creative_orientation_exclusions || [],
      iabCategories: apiObject.inventory_targeting?.iab_categories || [],
      iabCategoryExclusions: apiObject.inventory_targeting?.iab_category_exclusions || [],
      languageIDs: apiObject.inventory_targeting?.language_ids || [],
      languageExclusionIDs: apiObject.inventory_targeting?.language_exclusion_ids || [],
      locationIDs: apiObject.inventory_targeting?.location_ids || [],
      locationExclusionIDs: apiObject.inventory_targeting?.location_exclusion_ids || [],
      geofenceGroupIDs: apiObject.inventory_targeting?.geofencegroup_ids || [],
      geofenceGroupExclusionIDs: apiObject.inventory_targeting?.geofencegroup_exclusion_ids || [],
      maxBidCPM: apiObject.max_bid_cpm,
      marketIDs: apiObject.inventory_targeting?.market_ids || [],
      marketExclusionIDs: apiObject.inventory_targeting?.market_exclusion_ids || [],
      mediaTypeIDs: apiObject.inventory_targeting?.mediatype_ids,
      mediaTypeExclusionIDs: apiObject.inventory_targeting?.mediatype_exclusion_ids || [],
      modifiedOnUtc:
        apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      networkIDs: apiObject.inventory_targeting?.network_ids || [],
      networkExclusionIDs: apiObject.inventory_targeting?.network_exclusion_ids || [],
      siteIDs: apiObject.inventory_targeting?.site_ids || [],
      siteExclusionIDs: apiObject.inventory_targeting?.site_exclusion_ids || [],
      unitIDs: apiObject.inventory_targeting?.unit_ids || [],
      unitExclusionIDs: apiObject.inventory_targeting?.unit_exclusion_ids || [],
      unitTags: apiObject.inventory_targeting?.unit_tags || [],
      unitTagExclusions: apiObject.inventory_targeting?.unit_tag_exclusions || [],
      unitPackIDs: apiObject.inventory_targeting?.unitpack_ids || [],
      unitPackExclusionIDs: apiObject.inventory_targeting?.unitpack_exclusion_ids || [],
      creativeFormats: apiObject.inventory_targeting?.creative_formats || [],
      creativeFormatExclusions: apiObject.inventory_targeting?.creative_format_exclusions || [],
      daypart: apiObject.daypart,
      purchaseType: apiObject.purchase_type,
      mediaOwnerIDs: apiObject.inventory_targeting?.owner_ids,
      organizationID: apiObject.organization_id,
      accountID: apiObject.account_id,
      account: apiObject.account != null ? Account.fromApi(apiObject.account) : null,
      advertiser: apiObject.advertiser != null ? Advertiser.fromApi(apiObject.advertiser) : null,
      advertiserID: apiObject.advertiser_id,
      createdOn: apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      networkFrequencyCaps: apiObject.plan_networkfreqcaps?.map((x) => NetworkFrequencyCap.fromApi(x)),
      isMeasurementRequested: apiObject.measurement_requested,
      minSecondsBetweenPlays: apiObject.min_seconds_between_plays,
      minConcentrationFactor: apiObject.min_concentration_factor,
      minConcentrationValue: apiObject.min_concentration_value,
      adDuration: apiObject.ad_duration,
      createdByUserID: apiObject.created_by,
      heatmaps: apiObject.heatmap_group?.heatmaps?.map((hm) => PlanHeatmap.fromApi(hm)),
      sharedEntity:
        apiObject.shared_entity != null ? SharedPublicEntity.fromSharedPublicEntityApi(apiObject.shared_entity) : null,
      planApprovals:
        apiObject.plan_approvals?.length > 0
          ? apiObject.plan_approvals.map((x) => PlanApproval.fromApi(x))?.reverse()
          : [],
      packageGroupID: apiObject.packagegroup_id,
      packageGroupDealsIDs: apiObject?.packagegroup?.package_ids || null,
      packageGroupDealsIDsMapping: apiObject?.packagegroup?.package_deal_ids || null,
      breakdownType: apiObject.breakdown_type,
      parentPlanID: apiObject.parent_plan_id,
      children: apiObject.children?.filter((x) => !x.archived),
      planTargetingCriteria:
        apiObject.plan_targetingcriteria != null
          ? apiObject.plan_targetingcriteria.map((x) => PlanTargetingCriterion.fromApi(x))
          : [],
      campaigns:
        apiObject.campaigns != null
          ? apiObject.campaigns.filter((x) => !x.archived).map((x) => Campaign.fromApi(x))
          : null,
      sharedPlanID: apiObject.sharedplan_id,
      packageID: apiObject.package_id,
      buyFromDealsEnabled: apiObject.buy_from_deals_enabled,
      ttdCampaign: apiObject.ttd_campaign != null ? TTDCampaign.fromApi(apiObject.ttd_campaign) : null,
      goal: apiObject.goal,
      journeyWorkflowConfigurationId: apiObject.journey_workflowconfiguration_id,
    };

    if (obj.planFee != null) {
      obj.planFee = {
        ...apiObject.plan_fee,
        value: apiObject.plan_fee.type === FeeType.Percent ? apiObject.plan_fee.value * 100 : apiObject.plan_fee.value,
      };
    }

    const plan = Plan.fromObject(obj);
    plan.apiObject = apiObject;

    if (plan.CPM == null && !plan.isCPMFromRateCard) {
      plan.CPM = obj.cpmNet != null && obj.cpmNet.length > 0 ? obj.cpmNet[0].cpm : 0;
    }

    return plan;
  }

  toApi(): IPlanAPI {
    const apiObject: IPlanAPI = {
      plan_id: this.id >= 0 ? this.id : undefined,
      name: this.name,
      uuid: this.uuid,
      start_date: toServerDate(this.startDate),
      end_date: toServerDate(this.endDate),
      budget: this.budget !== null && this.isBudgetModeAmount ? this.budget : null,
      budget_mode: this.budgetMode !== null ? this.budgetMode : BudgetModes.BUDGET,
      cpm_gross: this.isCPMFromRateCard ? null : this.CPM,
      cpm: this.isCPMFromRateCard ? null : this.CPM,
      plan_fee: this.planFee,
      max_bid_cpm: this.maxBidCPM,
      is_cpm_from_rate_card: this.isCPMFromRateCard,
      targeted_impressions: this.isBudgetModeImpressions ? parseInt(`${this.targetImpressions}`, 10) : null,
      inventory_targeting: {
        market_ids: this.marketIDs,
        market_exclusion_ids: this.marketExclusionIDs,
        owner_ids: this.mediaOwnerIDs ?? [],
        creative_orientations: this.creativeOrientations,
        creative_orientation_exclusions: this.creativeOrientationExclusions,
        iab_categories: this.iabCategories,
        iab_category_exclusions: this.iabCategoryExclusions,
        language_ids: this.languageIDs,
        language_exclusion_ids: this.languageExclusionIDs,
        location_ids: this.locationIDs,
        location_exclusion_ids: this.locationExclusionIDs,
        geofencegroup_ids: this.geofenceGroupIDs,
        geofencegroup_exclusion_ids: this.geofenceGroupExclusionIDs,
        mediatype_ids: this.mediaTypeIDs,
        mediatype_exclusion_ids: this.mediaTypeExclusionIDs,
        network_ids: this.networkIDs,
        network_exclusion_ids: this.networkExclusionIDs,
        site_ids: this.siteIDs,
        site_exclusion_ids: this.siteExclusionIDs,
        unit_ids: this.unitIDs,
        unit_exclusion_ids: this.unitExclusionIDs,
        unit_tags: this.unitTags,
        unit_tag_exclusions: this.unitTagExclusions,
        unitpack_ids: this.unitPackIDs,
        unitpack_exclusion_ids: this.unitPackExclusionIDs,
        creative_formats: this.creativeFormats,
        creative_format_exclusions: this.creativeFormatExclusions,
      },
      modified_on_utc: toServerDateTime(this.modifiedOnUtc),
      daypart: this.daypart,
      purchase_type: this.purchaseType,
      demographic_ids: this.demographicIDs,
      plan_properties: this.planProperties.map((x) => x.toApi()),
      account_id: this.accountID,
      advertiser_id: this.advertiserID,
      created_on_utc: toServerDateTime(this.createdOn),
      plan_networkfreqcaps: this.networkFrequencyCaps.map((x) => x.toApi()),
      min_seconds_between_plays: this.minSecondsBetweenPlays,
      min_concentration_factor: this.minConcentrationFactor,
      ad_duration: this.adDuration ?? 0,
      heatmap_group: { heatmaps: this.heatmaps?.map((hm) => hm.toApi()) },
      packagegroup_id: this.packageGroupID,
      breakdown_type: this.breakdownType,
      parent_plan_id: this.parentPlanID,
      plan_targetingcriteria:
        this.planTargetingCriteria != null ? this.planTargetingCriteria.map((x) => x.toApi()) : undefined,
      measurement_requested: this.isMeasurementRequested,
      package_id: this.packageID,
      buy_from_deals_enabled: this.buyFromDealsEnabled,
      goal: this.goal,
      journey_workflowconfiguration_id: this.journeyWorkflowConfigurationId,
    };

    if (Plan.isTTDOrganization) {
      apiObject.advertiser_id = null;
      apiObject.ttd_campaign = TTDCampaign.toAdvertiserApi(this.ttdCampaign as unknown as Advertiser);
    }

    if (this.minConcentrationValue !== null) {
      apiObject.min_concentration_value = this.minConcentrationValue;
    }

    if (this.planFee != null) {
      apiObject.plan_fee = {
        ...this.planFee,
        value: this.isPercentMarkup ? this.planFee.value / 100 : this.planFee.value,
      };
    }

    if (this.isInlineEdited) {
      Plan.expandedProperties.forEach((property) => delete apiObject[property]);
    }

    return apiObject;
  }

  async getChanges(): Promise<IEntityChange<Plan>[]> {
    if (this.isNew) return [];

    return super.getValidatableChanges<Plan>(this.originalObject, Plan.entityProperties, [
      this.budgetMode === BudgetModes.BUDGET ? 'targetImpressions' : 'budget',
      // 'attachedMeasurements',
      // 'audienceFile',
    ]);
  }

  static fromObject(obj?): Plan {
    return super.fromObject(obj, new Plan());
  }

  get purchaseTypeName(): string {
    const purchaseTypeOptions = PurchaseTypeOptions.planOptions();

    // When a purchase type is disabled from the org, it will throw an error in download/ list's view as purchase type will be missing from all options.
    const option = purchaseTypeOptions.find((x) => x.value === this.purchaseType);
    return option?.name ?? DASH;
  }

  get hasCadencePurchaseType(): boolean {
    return this.purchaseType === PurchaseTypes.CADENCE;
  }

  get isTakeoverEnabled(): boolean {
    return this.purchaseType === PurchaseTypes.TAKEOVER;
  }

  get isHouseAdEnabled(): boolean {
    return this.purchaseType === PurchaseTypes.HOUSE_AD;
  }

  get isBudgetEditable(): boolean {
    return this.isBudgetModeAmount;
  }

  get isMarketTargetedImpressionsEditable(): boolean {
    return this.isBudgetModeImpressions;
  }

  get hasDeliveredImpressions(): boolean {
    return false;
  }

  get isAdSpendCadenceWithDeliveredImpressions(): boolean {
    return false;
  }

  get isFromMargin(): boolean {
    return false;
  }

  get status(): string {
    return this.planApprovals[0]?.status;
  }

  get statusFormatted(): string {
    return capitalize(this.planApprovals[0]?.statusFormatted ?? DASH);
  }

  get sharedPlanURL(): string {
    if (this.sharedPlanID != null) {
      return `/proposal/${this.uuid}`;
    }
    return null;
  }

  get campaignLinkedURL(): string {
    if (this.campaigns?.length > 0) {
      return `/campaigns/${this.campaigns[0].id}`;
    }
    return null;
  }

  get package() {
    return this.packageInternal;
  }

  set package(x: Package) {
    rootStore.commit(`${UPDATE_PLAN_PACKAGE}`, {
      plan: this,
      value: x,
    });
    rootStore.commit(`${UPDATE_PLAN_CPM_CALCULATOR}`, {
      plan: this,
      value: new CPMCalculator(x, null, this),
    });
  }

  getExtraValidation() {
    const organizationSettings = rootState.orgApp.organizationSettings;
    const extras: Record<string, IExtraValidation> = {};

    //
    // Purchase Type
    //
    if (this.purchaseType == null) {
      extras.purchaseType = {
        value: this.purchaseType,
        rules: 'required|not_empty',
      };
    }

    //
    // Make sure a CPM is specified in "budget" mode
    //
    if (
      !this.isCPMFromRateCard &&
      !organizationSettings.allowZeroFixedRateCPM &&
      this.CPM === 0 &&
      this.isBudgetModeAmount &&
      this.budget !== null &&
      this.budget !== 0
    ) {
      extras.CPM = {
        message: i18n.global.t('messages.cpmMustBeSpecifiedInBudgetMode'),
        preEvaluated: true,
      };
    }

    //
    // Budget
    //
    if (this.budget != null && !Number.isNaN(this.budget)) {
      if (this.cpmCalculator.budgetFormatted.includes(' – ')) {
        extras.budget = {
          value: parseInt(this.cpmCalculator.budgetFormatted.split(' – ')[0].split('.')[0].replace(/\D/g, ''), 10),
          rules: 'max_value:2000000000',
        };
      } else {
        extras.budget = {
          value: this.budget,
          rules: 'max_value:2000000000',
        };
      }
    }

    //
    // Impressions
    //
    if (this.targetImpressions != null) {
      extras.targetImpressions = {
        value: this.targetImpressions,
        rules: 'max_value:2000000000',
      };
    }

    if (this.isNew && this.startDate < DateTime.local().startOf('day')) {
      extras.startDate = {
        message: i18n.global.t('messages.planStartDateCannotBeSetInThePast'),
        preEvaluated: true,
      };
    }

    //
    // Make sure at least one network is selected whenever the setting to force its selection is on.
    //
    if (
      organizationSettings.uiForceNetworkSelectionInLineitemsAndDeals &&
      this.networkIDs.length === 0 &&
      this.purchaseType !== PurchaseTypes.HOUSE_AD
    ) {
      extras.networkIDs = {
        message: i18n.global.t('messages.youMustSelectAMininumOfOneNetwork'),
        preEvaluated: true,
      };
    }

    // Ad Duration
    extras.adDuration = {
      value: this.adDuration,
      rules: 'numeric:int|min_value:0|max_value:180',
    };

    // % Markup cpm
    if (this.hasMarkup && this.isPercentMarkup) {
      extras.percentMarkup = {
        value: this.planFee.value,
        rules: 'numeric:float|min_value:0|max_value:100',
      };
    }

    // Goal
    extras.goal = {
      value: this.goal,
      rules: 'required|not_empty',
    };

    return extras;
  }

  static validatorCreateRules() {
    return {
      name: 'required|max:191|not_empty_or_whitespace',
      startDate: 'required|date_time|before_or_equal:endDate',
      endDate: 'required|date_time|after_or_equal:startDate',
      minSecondsBetweenPlays: 'numeric:int|min_value:0|max_value:3600',
    };
  }

  static get expandedProperties() {
    return [];
  }

  static get entityProperties() {
    if (Plan.entityPropertiesCached != null) {
      return Plan.entityPropertiesCached;
    }
    Plan.entityPropertiesCached = getEntityProperties();
    return Plan.entityPropertiesCached;
  }

  static get expands(): string[] {
    const expands = [
      'inventory_targeting',
      'advertiser.unique_advertiser',
      'account',
      'plan_demographictargets',
      'plan_networkfreqcaps',
      'plan_targetingcriteria/plantargetingcriterion_values',
      'plan_properties',
      'heatmap_group.heatmaps',
      'plan_approvals',
      'shared_entity',
      'campaigns',
    ];

    const expandsChildren = [
      'children.inventory_targeting',
      'children.plan_targetingcriteria/plantargetingcriterion_values',
      'children.plan_properties',
      'children.plan_networkfreqcaps',
      'children.plan_demographictargets',
      'children.advertiser.unique_advertiser',
    ];

    if (Plan.isDemandOrganization) {
      expands.push('packagegroup.package_deal_ids', 'plan_fee');
      expandsChildren.push('children.plan_fee');
    }

    if (Plan.isTTDOrganization) {
      expands.push('ttd_campaign');
    }

    return expands.concat(expandsChildren);
  }

  static get lightExpands(): string[] {
    return Plan.expands.filter((x) => x !== 'heatmap_group.heatmaps' && x !== 'shared_entity');
  }

  static get entityName(): string {
    return type;
  }

  static async getEntities(ids): Promise<Plan[]> {
    if (ids.length === 0) return [];

    try {
      const qp = QueryParams.all(FilterCollection.for('plan_id', ids, FilterComparisonOperator.IN));
      qp.expand = Plan.expands;
      qp.showLoader = false;
      const getPlansAction = ({ queryParams, cancelTokenSource, showLoader }) =>
        rootStore.dispatch('orgApp/getPlans', { queryParams, cancelTokenSource, showLoader });
      const response = await qp.run(getPlansAction);
      return response.items;
    } catch (e) {
      throw new Exception('Failed to get lineItems.');
    }
  }
}
